import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import type { OrderedFields } from '@/hooks/useForm/types';
import { cn } from '@/lib/cn';
import { ObjectEntries, ObjectValues } from '@/lib/utils';
import type { BaseInstance } from '@pigello/pigello-matrix';
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch, type Path } from 'react-hook-form';
import type { FieldProps } from '../types';
import { DescriptionTooltip } from './description-tooltip';
import {
  COMPANY_TENANT_BANKS,
  createFMI,
  getBankFromFMI,
  PRIVATE_TENANT_BANKS,
} from '@/lib/fmi';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Icons } from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

export function FMIField<Instance extends BaseInstance = BaseInstance>({
  formField,
  name,
  placeholder,
  label,
  description,
  horizontal,
  className,
  disabled,
  overrideRequired,
  canHandleField,
  orderedFields,
  onValueChange,
}: FieldProps<Instance> & {
  orderedFields: OrderedFields<string, Instance>;
  onValueChange?: (value: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const formContext = useFormContext();
  const btnRef = useRef<HTMLButtonElement | null>(null);

  const isCompany = Boolean(
    ObjectValues(orderedFields).find((value) =>
      value.find((innerValue) => innerValue.name === 'corporateName')
    )
  );

  const identifier = useWatch({ name: isCompany ? 'orgNo' : 'ssn' });
  const [selectValue, setSelectValue] = useState<string>(
    getBankFromFMI(formContext.getValues(formField.name.toString()) ?? '')
  );

  useEffect(() => {
    formContext.setValue(
      formField.name.toString(),
      selectValue === '' ? '' : createFMI(selectValue, identifier, isCompany),
      {
        shouldDirty: true,
      }
    );
  }, [selectValue, identifier, formContext, formField.name, isCompany]);

  return (
    <FormField
      control={formField.control}
      rules={{
        required:
          (overrideRequired != null ? overrideRequired : formField.required) &&
          'Detta fält är obligatoriskt',
        validate: () => {
          if (selectValue) {
            if (!identifier) {
              return (isCompany ? 'Organisationsnummer' : 'Personnummer') +
                ' saknas, lägg till ' +
                isCompany
                ? 'Organisationsnummer'
                : 'Personnummer' + ' eller rensa fältet.';
            }
          }
          return true;
        },
      }}
      name={name as Path<Instance>}
      render={({ field }) => (
        <FormItem
          className={cn(
            'flex w-full flex-col items-center space-y-2',
            horizontal && 'flex-row justify-between',
            className
          )}
        >
          <div className={cn('grid', horizontal ? 'w-1/2 shrink-0' : 'w-full')}>
            <div className='flex items-center'>
              <FormLabel>
                {label ?? formField.label}
                {(overrideRequired != null
                  ? overrideRequired
                  : formField.required) && <span>*</span>}
              </FormLabel>

              {(description || formField.description) && (
                <DescriptionTooltip
                  description={description ?? formField.description}
                />
              )}
            </div>
            <FormDescription>{`Kräver giltigt ${isCompany ? 'org.nr' : 'pers.nr'}, fylls i separat fällt.\n${field.value && `(${field.value})`}`}</FormDescription>
            {horizontal && <FormMessage />}
          </div>
          <Popover modal={true} open={open} onOpenChange={setOpen}>
            <FormControl>
              <PopoverTrigger asChild ref={btnRef}>
                <Button
                  disabled={disabled ?? field.disabled ?? !canHandleField}
                  variant='outline'
                  ref={btnRef}
                  role='listbox'
                  className={cn(
                    'h-11 w-full items-center justify-between rounded-input px-4 leading-relaxed shadow-none'
                  )}
                >
                  {field.value !== '' ? (
                    <span>
                      {
                        ObjectEntries(
                          isCompany
                            ? COMPANY_TENANT_BANKS
                            : PRIVATE_TENANT_BANKS
                        ).find(([_, value]) => value === selectValue)?.[0]
                      }
                    </span>
                  ) : (
                    <span className='text-muted-foreground opacity-60'>
                      {placeholder ?? 'Välj bank'}
                    </span>
                  )}
                  <div className='flex items-center'>
                    {field.value !== '' && (
                      <span
                        title='Rensa fältet'
                        onKeyDown={(evt) => {
                          if (evt.key === 'Enter') {
                            evt.stopPropagation();
                            evt.preventDefault();
                            setSelectValue('');
                            onValueChange?.(null);
                          }
                        }}
                        role='button'
                        tabIndex={0}
                        onClick={(evt) => {
                          evt.stopPropagation();
                          evt.preventDefault();
                          setSelectValue('');
                          onValueChange?.(null);
                        }}
                      >
                        <Icons.close className='size-4 opacity-50 transition-opacity hover:opacity-100' />
                      </span>
                    )}
                    <Icons.selectIndicator className='ml-2 size-4 shrink-0 opacity-50' />
                  </div>
                </Button>
              </PopoverTrigger>
            </FormControl>
            <PopoverContent
              style={{ width: btnRef.current?.offsetWidth }}
              className='p-0 pb-1'
            >
              <Command
                loop={true}
                filter={(value, search, keywords) => {
                  const extendValue = value + ' ' + keywords?.join(' ');
                  if (extendValue.includes(search)) return 1;
                  return 0;
                }}
              >
                <CommandInput placeholder={placeholder ?? 'Välj bank'} />
                <CommandList>
                  <CommandEmpty>Inga resultat hittades</CommandEmpty>
                  <CommandGroup className='max-h-60 w-auto overflow-y-scroll'>
                    {ObjectEntries(
                      isCompany ? COMPANY_TENANT_BANKS : PRIVATE_TENANT_BANKS
                    ).map(([key, value]) => (
                      <CommandItem
                        keywords={[value, key, key.toLowerCase()]}
                        key={value}
                        value={value}
                        onSelect={() => {
                          setSelectValue(value);
                          setOpen(false);
                          onValueChange?.(value);
                        }}
                      >
                        <Icons.check
                          className={cn(
                            'mr-2 size-4',
                            value === field.value?.id
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                        {key}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {!horizontal && <FormMessage className='self-start' />}
        </FormItem>
      )}
    />
  );
}
